/**
 * LiveCoachEncouragement — transient pop-in showing one of Trouvé's
 * short positive-reinforcement lines during a Learn-with-Trouvé
 * session. Triggered by the parent (LiveCoach.jsx) when a step is
 * marked done.
 *
 * Frequency: parent decides WHEN to call (typically on every other
 * step completion). This component just renders whatever message it
 * was last given, fading in/out for 2.5s.
 *
 * Voice: if TTS is enabled and a `tts` instance is passed, the line is
 * also spoken aloud — Trouvé voice + on-screen text reinforce each
 * other, matching the founder's spec ("contextual encouragement from
 * Trouvé during routines").
 *
 * The string pool lives in i18n under `liveCoach.encouragement.lines`
 * so it's translated for FR/ES/PT users. Mirrors the backend's
 * TROUVE_ENCOURAGEMENT_BANK in trouve_persona.py — keep the two banks
 * in lock-step if you tweak one.
 */
import React, { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { motion, AnimatePresence } from "framer-motion";

// Number of lines in the encouragement bank — must match the count of
// `liveCoach.encouragement.line1`..`lineN` keys in each locale file.
const ENCOURAGEMENT_LINE_COUNT = 10;

/** Pick one random encouragement line key. Pure function. */
export const pickRandomEncouragementKey = () => {
  const n = Math.floor(Math.random() * ENCOURAGEMENT_LINE_COUNT) + 1;
  return `liveCoach.encouragement.line${n}`;
};

const LiveCoachEncouragement = ({ messageKey, onDismiss, tts }) => {
  const { t } = useTranslation();

  // Auto-dismiss after 2.5s. Also speak the line if TTS is on.
  useEffect(() => {
    if (!messageKey) return;
    const line = t(messageKey);
    if (tts && tts.enabled && line) {
      try {
        tts.speak(line);
      } catch (_) {
        // TTS failure is not fatal — text still shows.
      }
    }
    const timer = setTimeout(() => onDismiss?.(), 2500);
    return () => clearTimeout(timer);
  }, [messageKey, t, tts, onDismiss]);

  return (
    <AnimatePresence>
      {messageKey && (
        <motion.div
          key={messageKey}
          initial={{ opacity: 0, y: -8, scale: 0.96 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, scale: 0.96 }}
          transition={{ duration: 0.3, ease: "easeOut" }}
          data-testid="live-coach-encouragement"
          className="pointer-events-none absolute top-20 left-1/2 -translate-x-1/2 z-30"
        >
          <div className="bg-gradient-to-br from-[#D5A8A4] to-[#9B6A6C] text-white px-5 py-2.5 rounded-full shadow-[0_12px_40px_-12px_rgba(155,106,108,0.7)]">
            <span className="font-serif italic text-base">
              {t(messageKey)}
            </span>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default LiveCoachEncouragement;
